import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Grid,
  LinearProgress,
  Typography,
} from '@mui/material';
import { AdminUserSummary, UpdateUserPayload, useUserRoles, useSetUserRoles } from '../../../services/adminUsers';

const ROLE_OPTIONS = ['employee', 'manager', 'finance', 'admin', 'super_admin'] as const;
const SCOPE_OPTIONS = ['none', 'team', 'all'] as const;

export interface EditUserDialogProps {
  open: boolean;
  onClose: () => void;
  user: AdminUserSummary | null;
  onSave: (payload: UpdateUserPayload) => Promise<void> | void;
}

export const EditUserDialog: React.FC<EditUserDialogProps> = ({ open, onClose, user, onSave }) => {
  const [roles, setRoles] = useState<string[]>(['employee']);
  const [department, setDepartment] = useState<string>('');
  const [isActive, setIsActive] = useState<boolean>(true);
  const [isApprover, setIsApprover] = useState<boolean>(false);
  const [approvalScope, setApprovalScope] = useState<string>('none');
  const [approvalLimit, setApprovalLimit] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const { data: fetchedRoles } = useUserRoles(user?.id);
  const setRolesMutation = useSetUserRoles();

  useEffect(() => {
    if (user) {
      // Initialize roles from server hook (preferred) or legacy single role
      if (Array.isArray(fetchedRoles) && fetchedRoles.length > 0) {
        setRoles(fetchedRoles);
      } else if (user.role) {
        setRoles([user.role]);
      } else {
        setRoles(['employee']);
      }
      setDepartment(user.department || '');
      setIsActive(user.is_active);
      setIsApprover(user.is_approver);
      setApprovalScope(user.approval_scope || 'none');
      setApprovalLimit(user.approval_limit ? String(user.approval_limit) : '');
    }
  }, [user, fetchedRoles]);

  const scopeDisabled = useMemo(() => !isApprover, [isApprover]);
  const limitDisabled = useMemo(() => !isApprover || approvalScope === 'none', [isApprover, approvalScope]);

  // Compute dirty state by comparing current values with initial user values
  const isDirty = useMemo(() => {
    if (!user) return false;
    const initLimit = user.approval_limit ? String(user.approval_limit) : '';
    const initialRoles = Array.isArray(fetchedRoles) && fetchedRoles.length > 0 ? fetchedRoles : (user.role ? [user.role] : ['employee']);
    const rolesChanged = initialRoles.sort().join(',') !== [...roles].sort().join(',');
    return (
      rolesChanged ||
      (user.department || '') !== department ||
      user.is_active !== isActive ||
      user.is_approver !== isApprover ||
      (user.approval_scope || 'none') !== approvalScope ||
      initLimit !== approvalLimit
    );
  }, [user, roles, fetchedRoles, department, isActive, isApprover, approvalScope, approvalLimit]);

  // Basic validity
  const isValid = useMemo(() => {
    if (!limitDisabled) {
      const n = Number(approvalLimit);
      if (!approvalLimit || Number.isNaN(n) || n <= 0) return false;
    }
    return true;
  }, [limitDisabled, approvalLimit]);

  // Inline error message for approval limit
  const limitError = useMemo(() => {
    if (limitDisabled) return '';
    if (!approvalLimit) return 'Approval limit is required when scope is not none.';
    const n = Number(approvalLimit);
    if (Number.isNaN(n)) return 'Approval limit must be a number.';
    if (n <= 0) return 'Approval limit must be greater than 0.';
    return '';
  }, [limitDisabled, approvalLimit]);

  const handleSave = async () => {
    if (!isDirty || !isValid || saving) return;
    setSaving(true);
    setErrorMsg(null);
    const payload: UpdateUserPayload = {
      department: department || null,
      is_active: isActive,
      is_approver: isApprover,
      approval_scope: (approvalScope as any),
      approval_limit: limitDisabled ? null : Number(approvalLimit || 0),
    };

    try {
      // 1) Save roles first
      if (user?.id) {
        await setRolesMutation.mutateAsync({ userId: user.id, roles });
      }
      // 2) Save other fields
      await onSave(payload);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      container={() => document.getElementById('root') as HTMLElement}
    >
      <DialogTitle>Edit User</DialogTitle>
      <DialogContent dividers>
        {saving && <LinearProgress />}
        {errorMsg && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {errorMsg}
          </Typography>
        )}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <TextField
              label="Roles"
              select
              fullWidth
              SelectProps={{ multiple: true, renderValue: (selected) => (selected as string[]).join(', ') }}
              value={roles}
              onChange={(e) => {
                const value = e.target.value;
                setRoles(typeof value === 'string' ? value.split(',') : (value as string[]));
              }}
              disabled={saving}
              helperText="Assign one or more roles. E.g., manager and super_admin."
            >
              {ROLE_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Department"
              fullWidth
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              disabled={saving}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={<Checkbox checked={isActive} onChange={(e) => setIsActive(e.target.checked)} disabled={saving} />}
              label="Active"
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={<Checkbox checked={isApprover} onChange={(e) => setIsApprover(e.target.checked)} disabled={saving} />}
              label="Is Approver"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Approval Scope"
              select
              fullWidth
              value={approvalScope}
              onChange={(e) => setApprovalScope(e.target.value)}
              disabled={scopeDisabled || saving}
            >
              {SCOPE_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Approval Limit"
              type="number"
              inputProps={{ min: 0, step: 0.01 }}
              fullWidth
              value={approvalLimit}
              onChange={(e) => setApprovalLimit(e.target.value)}
              disabled={limitDisabled || saving}
              error={!limitDisabled && !!limitError}
              helperText={!limitDisabled ? (limitError || 'Enter a positive amount') : ''}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={async () => {
            try {
              await handleSave();
            } catch (e: any) {
              // Capture error message if onSave throws
              const msg = e?.userMessage || e?.response?.data?.detail || e?.message || 'Failed to save user';
              setErrorMsg(String(msg));
            }
          }}
          disabled={!isDirty || !isValid || saving}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditUserDialog;
